import { imageDataToBase64 } from '@/common/images';
import { Camera } from '@/components/camera/component';
import { useFacility } from '@/contexts/facility-context/hook';
import { Flex, Loader, Text, Title } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';
import { AssetStatus } from '../../../../../../../backend/src/types.generated';
import { useFetchBagsForBaleCreateLazyQuery } from '../graphql.generated';
import { useBaleCreateContext } from '../hook';
import styles from '../styles.module.css';

export const BaleCreateAddPhotoPage = () => {
	const { setPage, setPhoto, currentBale, setCurrentBale } = useBaleCreateContext();
	const { currentFacility } = useFacility();
	const [loading, setLoading] = useState(false);
	const [fetchBags] = useFetchBagsForBaleCreateLazyQuery();

	const handlePhotoAccepted = async (imageData: ImageData) => {
		if (!currentFacility || !currentBale?.material) return;
		setLoading(true);
		const photoDataUrl = `data:image/jpeg;base64,${imageDataToBase64(imageData, 'jpeg', 0.99)}`;
		setPhoto(photoDataUrl);
		const { data } = await fetchBags({
			variables: {
				filter: {
					scanEvents: {
						facility: {
							id: currentFacility.id,
						},
					},
					actualMaterial: currentBale.material,
					status: AssetStatus.BagReceived,
				},
			},
			fetchPolicy: 'network-only',
		});

		setCurrentBale((prev) =>
			prev ? { ...prev, bags: data?.trackableAssets_aggregate?.count ?? null } : null
		);

		setLoading(false);
		setPage('CONFIRM');
	};

	return (
		<Flex pos="absolute" h="100vh" w="100%" className={styles.pageBackground}>
			<Flex
				className={styles.photoPageHeader}
				pos="fixed"
				top={0}
				left={0}
				px="md"
				py="lg"
				bg="white"
				w="100%"
				align="center"
				gap="sm"
			>
				<IconArrowLeft onClick={() => setPage('IDENTIFY')} />
				<Title order={2}>Add Photo</Title>
			</Flex>
			<Camera onTakePhoto={handlePhotoAccepted} />
			<AnimatePresence>
				{loading && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{
							opacity: 1,
							transition: {
								duration: 0.4,
								ease: [0.25, 0.1, 0.25, 1],
							},
						}}
						exit={{
							opacity: 0,
							transition: {
								duration: 0.3,
								ease: [0.25, 0.1, 0.25, 1],
							},
						}}
						className={styles.loadingOverlay}
					>
						<Flex h="100%" w="100%" align="center" justify="center" direction="column" gap="md">
							<motion.div
								initial={{
									opacity: 0,
									scale: 0.8,
									y: 20,
								}}
								animate={{
									opacity: 1,
									scale: 1,
									y: 0,
									transition: {
										duration: 0.6,
										delay: 0.2,
										ease: [0.25, 0.1, 0.25, 1],
										type: 'spring',
										damping: 20,
										stiffness: 300,
									},
								}}
								exit={{
									opacity: 0,
									scale: 0.8,
									y: -20,
									transition: {
										duration: 0.3,
										ease: [0.25, 0.1, 0.25, 1],
									},
								}}
							>
								<Loader color="blue" />
							</motion.div>

							<motion.div
								initial={{
									opacity: 0,
									y: 10,
								}}
								animate={{
									opacity: 1,
									y: 0,
									transition: {
										duration: 0.5,
										delay: 0.4,
										ease: [0.25, 0.1, 0.25, 1],
									},
								}}
								exit={{
									opacity: 0,
									y: -10,
									transition: {
										duration: 0.3,
										ease: [0.25, 0.1, 0.25, 1],
									},
								}}
							>
								<Text size="md" ta="center" c="white">
									Calculating bale contents...
								</Text>
							</motion.div>
						</Flex>
					</motion.div>
				)}
			</AnimatePresence>
		</Flex>
	);
};
